import { OTHER_WORKSPACE_EVENT } from "./workspace-events";
import type { SavedVersion, SavedRun } from "../../../packages/contracts/src/workspace";
import type { JudgeRun, Judgment, MethodCorrections, MethodPlacement, MethodPlacements, Placement, Proposal, ProposalDetail } from "../../../packages/contracts/src/improvement";
import type { RunInspection } from "@method-ai-hq/sdk/inspection";
import type { Inbox, MethodOverview, SearchResults } from "../../../packages/contracts/src/dashboard";
import type { ConversionReport } from "@method-ai-hq/sdk/conversion";
import { LOCAL_METHOD_SERVER, type ComparisonRunView, type ComparisonSummary, type ComparisonView, type GraderAgreement, type LocalComparisonRun, type QuestionChoice } from "../../../packages/contracts/src/comparison";

export type WorkflowStatus = "ready" | "needs_review";


export type WorkflowSummary = {
  id: string;
  name: string;
  status: WorkflowStatus;
  desired_outcome: string | null;
  created_at: string;
  updated_at: string;
  /** Set by Archive. An archived Method is left out of the Methods lists until it is saved or run again. */
  archived_at?: string | null;
  /** The person's pin order, or null when not pinned. */
  pinned_position?: number | null;
  /** Recent runs, last result, facts and waiting changes. Null when the server could not read them. */
  overview?: MethodOverview | null;
  current_version_id?: string | null;
};

export type WorkflowDetail = WorkflowSummary & {
  version_id?: string | null;
  version_number?: number | null;
  source_url: string | null;
  artifact: {
    id: string;
    markdown: string;
    sha256: string;
    created_at: string;
  } | null;
  workflow_source: string | null;
  conversion_report: ConversionReport | null;
};

export type Session = {
  user: {
    id: string;
    organization_id: string;
    email: string;
    name: string;
  };
  /** This person may use God Mode. */
  staff: boolean;
  /** The person joined this workspace through an invitation and has not run a Method yet. */
  welcome: boolean;
  /** The staff member who is viewing this account now, read only. */
  viewing_as: { id: string; email: string; name: string } | null;
  organization: {
    id: string;
    name: string;
    keep_run_content_on_devices: boolean;
  };
};

export type Member = { id: string; name: string; email: string; joined_at: string; you: boolean };
export type Invitation = { id: string; email: string; invited_by: string; invited_at: string };

export type Billing = {
  enabled: boolean;
  plan: "free" | "startup";
  status: string | null;
  current_period_end: string | null;
  has_customer: boolean;
  features: string[];
  limits: { hosted_model_usd: number; hosted_model_reset: "monthly" | null };
  hosted_model: { used_usd: number | null; limit_usd: number; reset: "monthly" | null };
};

const apiBaseUrl = (import.meta.env?.VITE_API_BASE_URL ?? "").replace(/\/+$/, "");

// God Mode: the account this browser tab views instead of the person's own. The tab forgets it when it closes.
const viewAsKey = "method:view-as";
export function viewingAccount(): string | null {
  try { return window.sessionStorage.getItem(viewAsKey); } catch { return null; }
}
export function viewAccount(userId: string | null) {
  try { userId ? window.sessionStorage.setItem(viewAsKey, userId) : window.sessionStorage.removeItem(viewAsKey); } catch { /* the tab cannot store it */ }
}
/** The header that opens the viewed account on the API, or no header. */
export function viewAsHeaders(): Record<string, string> {
  const userId = viewingAccount();
  return userId ? { "x-method-view-as": userId } : {};
}

export type StaffAccount = {
  id: string; email: string; name: string; organization: string; created_at: string; last_seen_at: string;
  methods: number; runs: number; last_run_at: string | null;
};

export function apiUrl(path: string): string {
  return apiBaseUrl ? `${apiBaseUrl}${path}` : path;
}

/** A failed request, with the server's error code when it sent one. */
export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly code?: string, readonly workspace?: Workspace) {
    super(message);
    this.name = "ApiError";
  }
}
export type Workspace = { id: string; name: string; current: boolean };
export type WorkspaceInvitation = { id: string; workspace: string; invited_by: string; invited_at: string };

export class UnauthorizedError extends Error {
  constructor() {
    super("Sign-in required.");
    this.name = "UnauthorizedError";
  }
}

async function send(path: string, init?: RequestInit): Promise<Response> {
  const response = await fetch(apiUrl(path), {
    ...init,
    credentials: "include",
    headers: {
      ...init?.headers,
      ...viewAsHeaders(),
      "x-method-visibility": document.visibilityState,
      ...(init?.body instanceof FormData ? {} : { "content-type": "application/json" })
    }
  });
  if (response.status === 401) {
    window.dispatchEvent(new Event("workflow:unauthorized"));
    throw new UnauthorizedError();
  }
  return response;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await send(path, init);
  const body = await response.json().catch(() => ({})) as {
    error?: string;
    code?: string;
    message?: string;
    workspace?: { id: string; name: string };
  };
  if (!response.ok) {
    // A Method, run or comparison in another of the person's workspaces names that workspace, so the page can switch.
    const workspace = body.code === "in_other_workspace" && body.workspace ? { ...body.workspace, current: false } : undefined;
    if (workspace) window.dispatchEvent(new CustomEvent<Workspace>(OTHER_WORKSPACE_EVENT, { detail: workspace }));
    throw new ApiError(body.message || readableError(body.code ?? body.error) || "The request failed.", response.status, body.code ?? body.error, workspace);
  }
  return body as T;
}

export const api = {
  methodPackage(id: string, version: string) {
    return request<{ package: import('@method-ai-hq/sdk/method-package').MethodPackage | null }>(`/api/cli/methods/${encodeURIComponent(id)}?version=${encodeURIComponent(version)}`);
  },
  async scriptCards(id: string, version: string): Promise<import('./components/method/script-card').ScriptCard[]> {
    const response = await send(`/api/workspace/methods/${encodeURIComponent(id)}/versions/${encodeURIComponent(version)}/script-cards`);
    if (!response.ok) return [];
    const body = await response.json().catch(() => ({})) as { cards?: unknown };
    return Array.isArray(body.cards) ? body.cards : [];
  },
  async savedScript(id: string, version: string, hash: string) {
    const response = await send(`/api/cli/methods/${encodeURIComponent(id)}/files/${encodeURIComponent(hash)}?version=${encodeURIComponent(version)}`);
    if (!response.ok) throw new Error('Saved source is not available');
    const bytes = await response.arrayBuffer();
    const actual = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)), byte => byte.toString(16).padStart(2, '0')).join('');
    if (actual !== hash) throw new Error('Saved source does not match this version.');
    return new TextDecoder('utf-8', {fatal: true}).decode(bytes);
  },
  /** The server's default hosted model name; undefined when hosted models are unavailable. */
  async defaultModel(): Promise<string | undefined> {
    const response = await send("/api/cli/models/default");
    if (!response.ok) return undefined;
    const body = await response.json().catch(() => ({})) as { model?: unknown };
    return typeof body.model === "string" ? body.model : undefined;
  },
  async recordDashboardActivity(): Promise<void> {
    await send("/api/activity/dashboard", { method: "POST" });
  },
  async recordMethodDownload(id: string): Promise<void> {
    await send(`/api/workspace/methods/${encodeURIComponent(id)}/download-event`, { method: "POST" });
  },
  async versions(id: string): Promise<SavedVersion[]> { return (await request<{versions:SavedVersion[]}>(`/api/workspace/methods/${encodeURIComponent(id)}/versions`)).versions; },
  async version(id: string, version: string): Promise<SavedVersion> { return (await request<{version:SavedVersion}>(`/api/workspace/methods/${encodeURIComponent(id)}/versions/${encodeURIComponent(version)}`)).version; },
  async restore(id: string, version: string, current: string): Promise<SavedVersion> { return (await request<{version:SavedVersion}>(`/api/workspace/methods/${encodeURIComponent(id)}/restore`,{method:'POST',body:JSON.stringify({version_id:version,current_version_id:current})})).version; },
  async runs(workflowId?: string): Promise<SavedRun[]> { return (await request<{runs:SavedRun[]}>(`/api/workspace/runs${workflowId ? `?workflow_id=${encodeURIComponent(workflowId)}` : ''}`)).runs; },
  /** Run now: start the published version in Method Cloud with the inputs of this run. */
  async runNow(methodId: string, fromRunId: string): Promise<{ run_id: string }> {
    return request(`/api/methods/${encodeURIComponent(methodId)}/runs`, { method: "POST", body: JSON.stringify({ from_run_id: fromRunId }) });
  },
  async runOverview(): Promise<SavedRun[]> { return (await request<{runs:SavedRun[]}>("/api/workspace/runs?overview=true")).runs; },
  async run(id: string): Promise<SavedRun & {inspection:RunInspection}> { return (await request<{run:SavedRun & {inspection:RunInspection}}>(`/api/workspace/runs/${encodeURIComponent(id)}`)).run; },
  async runFileBytes(id:string, hash:string):Promise<Uint8Array> {
    const response = await fetch(`${apiBaseUrl}/api/workspace/runs/${encodeURIComponent(id)}/files/${encodeURIComponent(hash)}`,{credentials:"include",headers:viewAsHeaders()});
    if (!response.ok) throw Error(`Result file request failed: ${response.status}`);
    return new Uint8Array(await response.arrayBuffer());
  },
  async runValue(id: string, hash: string): Promise<unknown> {
    const response = await fetch(`${apiBaseUrl}/api/workspace/runs/${encodeURIComponent(id)}/values/${encodeURIComponent(hash)}`,{credentials:"include",headers:viewAsHeaders()});
    if (!response.ok) throw Error(`Step value request failed: ${response.status}`);
    return response.json();
  },
  async openRunWebsite(id: string, sha256: string): Promise<{url:string}> { return request(`/api/workspace/runs/${encodeURIComponent(id)}/website`, {method:'POST',body:JSON.stringify({sha256})}); },
  async importRun(id: string, version: string, inspection: unknown): Promise<SavedRun> { return (await request<{run:SavedRun}>(`/api/workspace/methods/${encodeURIComponent(id)}/runs`,{method:'POST',body:JSON.stringify({version_id:version,inspection})})).run; },
  /** A person's right or wrong on a run's result. A computer with Claude Code or Codex fixes a wrong one the next time the Method runs there. */
  async judgeRun(runId: string, body: JudgeRun): Promise<{judgment:Judgment;held:boolean}> { return request(`/api/runs/${encodeURIComponent(runId)}/judgments`, {method:'POST',body:JSON.stringify(body)}); },
  async runJudgments(runId: string): Promise<(Judgment & {pending:boolean})[]> { return (await request<{judgments:(Judgment & {pending:boolean})[]}>(`/api/runs/${encodeURIComponent(runId)}/judgments`)).judgments; },
  /** Keep this judgment of two that contradict each other; the other is set aside. */
  async settleJudgment(id: string): Promise<void> { await request(`/api/judgments/${encodeURIComponent(id)}/settle`, {method:'POST',body:JSON.stringify({keep:true})}); },
  /** The person's answer to Method's question about a correction. The next fix reads it. */
  async answerJudgment(id: string, answer: string): Promise<{judgment:Judgment}> { return request(`/api/judgments/${encodeURIComponent(id)}/answer`, {method:'POST',body:JSON.stringify({answer})}); },
  /** The Method page's corrections: open questions, what the agent learned, and how each fix held. */
  async methodCorrections(id: string): Promise<MethodCorrections> { return request<MethodCorrections>(`/api/methods/${encodeURIComponent(id)}/corrections`); },
  /** Remove a rule the agent learned: a new current version without it. */
  async removeLearnedRule(methodId: string, ruleId: string): Promise<{version_id:string;version_number:number}> { return request(`/api/methods/${encodeURIComponent(methodId)}/learned/${encodeURIComponent(ruleId)}/remove`, {method:'POST',body:'{}'}); },
  async proposal(id: string): Promise<ProposalDetail> { return (await request<{proposal:ProposalDetail}>(`/api/proposals/${encodeURIComponent(id)}`)).proposal; },
  async undoProposal(id: string): Promise<Proposal> { return (await request<{proposal:Proposal}>(`/api/proposals/${encodeURIComponent(id)}/undo`, {method:'POST',body:'{}'})).proposal; },
  async placements(id: string): Promise<MethodPlacements> { return request<MethodPlacements>(`/api/methods/${encodeURIComponent(id)}/placements`); },
  async setPlacement(id: string, environment: string, placement: Placement): Promise<MethodPlacement> { return request<MethodPlacement>(`/api/methods/${encodeURIComponent(id)}/placements/${encodeURIComponent(environment)}`, {method:'PUT',body:JSON.stringify({placement})}); },
  signInUrl(): string {
    return apiUrl("/auth/login");
  },

  async logout(): Promise<string> {
    const response = await request<{ logout_url?: string }>("/auth/logout", {
      method: "POST",
      body: JSON.stringify({})
    });
    return response.logout_url || "/";
  },

  async me(): Promise<Session> {
    return request<Session>("/api/me");
  },
  /** God Mode: every account, most recently seen first. Asked as the staff member, not the viewed account. */
  async staffAccounts(q: string): Promise<StaffAccount[]> {
    const response = await fetch(apiUrl(`/api/staff/accounts?q=${encodeURIComponent(q)}`), { credentials: "include" });
    const body = await response.json().catch(() => ({})) as { accounts?: StaffAccount[]; message?: string };
    if (!response.ok) throw new ApiError(body.message || "Could not load the accounts.", response.status);
    return body.accounts ?? [];
  },

  async billing(): Promise<Billing> {
    return request<Billing>("/api/billing");
  },
  /** A Stripe Checkout URL for the Startup plan. */
  async billingCheckout(): Promise<string> {
    return (await request<{ url: string }>("/api/billing/checkout", { method: "POST", body: "{}" })).url;
  },
  /** A Stripe Customer Portal URL. */
  async billingPortal(): Promise<string> {
    return (await request<{ url: string }>("/api/billing/portal", { method: "POST", body: "{}" })).url;
  },

  /** The person's workspaces, and open invitations to their email for other workspaces. */
  async workspaces(): Promise<{ workspaces: Workspace[]; invitations: WorkspaceInvitation[] }> {
    return request("/api/workspaces");
  },
  async createWorkspace(name: string): Promise<Workspace> {
    return (await request<{ workspace: Workspace }>("/api/workspaces", { method: "POST", body: JSON.stringify({ name }) })).workspace;
  },
  async joinWorkspace(invitationId: string): Promise<Workspace> {
    return (await request<{ workspace: Workspace }>(`/api/workspaces/invitations/${encodeURIComponent(invitationId)}/accept`, { method: "POST", body: "{}" })).workspace;
  },
  /** The browser address that signs in to another workspace of the person. */
  switchWorkspaceUrl(id: string): string {
    return apiUrl(`/auth/workspace/${encodeURIComponent(id)}`);
  },

  async setWorkspaceName(name: string): Promise<void> {
    await request("/api/organization", { method: "PATCH", body: JSON.stringify({ name }) });
  },
  async members(): Promise<{ members: Member[]; invitations: Invitation[] }> {
    return request("/api/members");
  },
  /** email_sent is false when the email could not go out; the person can still sign up with the address. */
  async invite(email: string): Promise<{ invitation: Invitation; email_sent: boolean }> {
    return request("/api/members/invitations", { method: "POST", body: JSON.stringify({ email }) });
  },
  async cancelInvitation(id: string): Promise<void> {
    await request(`/api/members/invitations/${encodeURIComponent(id)}/cancel`, { method: "POST", body: "{}" });
  },
  async removeMember(id: string): Promise<void> {
    await request(`/api/members/${encodeURIComponent(id)}/remove`, { method: "POST", body: "{}" });
  },

  async setKeepRunContentOnDevices(keep: boolean): Promise<void> {
    await request("/api/organization", { method: "PATCH", body: JSON.stringify({ keep_run_content_on_devices: keep }) });
  },

  /** Replace the person's pinned Methods with these, in this order. */
  async setPins(workflowIds: string[]): Promise<string[]> {
    return (await request<{ workflow_ids: string[] }>("/api/pins", { method: "PUT", body: JSON.stringify({ workflow_ids: workflowIds }) })).workflow_ids;
  },
  async setArchived(id: string, archived: boolean): Promise<void> {
    await request(`/api/methods/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify({ archived }) });
  },
  async comparisons(): Promise<ComparisonSummary[]> { return (await request<{ comparisons: ComparisonSummary[] }>("/api/comparisons")).comparisons; },
  /** A comparison's result page: the given version, or the newest. */
  async comparison(id: string, version?: number): Promise<ComparisonView> {
    return (await request<{ comparison: ComparisonView }>(`/api/comparisons/${encodeURIComponent(id)}${version ? `/versions/${version}` : ""}`)).comparison;
  },
  async comparisonRun(id: string, runId: string): Promise<ComparisonRunView> {
    return (await request<{ run: ComparisonRunView }>(`/api/comparisons/${encodeURIComponent(id)}/runs/${encodeURIComponent(runId)}`)).run;
  },
  /** One input of a comparison, by the hash of the content that the CLI uploaded. */
  async comparisonInput(id: string, sha256: string): Promise<string> {
    return (await request<{ text: string }>(`/api/comparisons/${encodeURIComponent(id)}/inputs/${encodeURIComponent(sha256)}`)).text;
  },
  async graderSeen(id: string, gradeKey: string): Promise<string> {
    return (await request<{ seen: string }>(`/api/comparisons/${encodeURIComponent(id)}/grades/${encodeURIComponent(gradeKey)}/seen`)).seen;
  },
  /** A person's right or wrong on one grader verdict of a run. It never starts a fix. */
  async markVerdict(runId: string, body: { comparison_id: string; grade: string; verdict: number; mark: "right" | "wrong" }): Promise<{ graders: GraderAgreement[] }> {
    return request(`/api/runs/${encodeURIComponent(runId)}/grader-marks`, { method: "POST", body: JSON.stringify(body) });
  },
  /** An answer to a question on a comparison's page. change and differently take the person's words. */
  async answerQuestion(id: string, body: { key: string; choice: QuestionChoice; words?: string }): Promise<void> {
    await request(`/api/comparisons/${encodeURIComponent(id)}/answers`, { method: "POST", body: JSON.stringify(body) });
  },
  /** Take back an answer: the question opens again. */
  async takeBackAnswer(id: string, key: string): Promise<void> {
    await request(`/api/comparisons/${encodeURIComponent(id)}/answers/${encodeURIComponent(key)}`, { method: "DELETE" });
  },
  /** A run whose content stays on the computer that ran it: its transcript and verdicts, from the local Method server there. */
  async localComparisonRun(localRunId: string): Promise<LocalComparisonRun> {
    const response = await fetch(`${LOCAL_METHOD_SERVER}/runs/${encodeURIComponent(localRunId)}/comparison`);
    if (!response.ok) throw new ApiError("The computer that ran this run did not answer.", response.status);
    return await response.json() as LocalComparisonRun;
  },
  async inbox(): Promise<Inbox> { return (await request<{ inbox: Inbox }>("/api/inbox")).inbox; },
  async search(query: string): Promise<SearchResults> { return (await request<{ results: SearchResults }>(`/api/search?q=${encodeURIComponent(query)}`)).results; },

  async list(): Promise<WorkflowSummary[]> {
    const workflows: WorkflowSummary[] = [];
    let cursor: string | null = null;
    do {
      const query = new URLSearchParams({ limit: "500" });
      if (cursor) query.set("cursor", cursor);
      const response = await request<{
        workflows: WorkflowSummary[];
        next_cursor: string | null;
      }>(`/api/methods?${query}`);
      workflows.push(...response.workflows);
      cursor = response.next_cursor;
    } while (cursor);
    return workflows;
  },

  async get(id: string): Promise<WorkflowDetail> {
    const response = await request<{ workflow: WorkflowDetail }>(
      `/api/methods/${encodeURIComponent(id)}`
    );
    return response.workflow;
  },

  async rename(id: string, name: string): Promise<void> {
    await request(`/api/methods/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify({ name })
    });
  },

};

function readableError(code: string | undefined): string | null {
  if (code === "workflow_program_not_ready") return "The method program is not ready yet.";
  if (code === "cloud_not_enabled") return "Method Cloud is not turned on for your organization.";
  return null;
}
